import { Phone, Code, QrCode, ArrowRight, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND_MASCOT_LARGE } from "@/data/brandMascots";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { Agent } from "@/types/agent/agent";
import { getReadableGenieBrandTheme, withAlpha } from "@/utils/branding/genieBrandTheme";

type DistributionModal = "phone" | "webEmbed" | "qr";

interface DistributionCardProps {
  agent: Agent;
  brand?: string;
  onAction: (modal: DistributionModal) => void;
}

const CHANNELS = [
  { id: "phone" as const, label: "Phone", icon: Phone, description: "Manage phone numbers and call routing" },
  { id: "webEmbed" as const, label: "Web Embed", icon: Code, description: "Embed your genie on any website" },
  { id: "qr" as const, label: "QR Code", icon: QrCode, description: "Share via scannable QR code" },
];

// A Listener Genie has no chat widget: it shares its recorder link and listens to calls on its own number.
const LISTENER_CHANNELS = [
  { id: "phone" as const, label: "Phone Listening", icon: Phone, description: "Record and analyse your business calls" },
  ...CHANNELS.filter(({ id }) => id === "qr"),
];

export const DistributionCard = ({ agent, brand = "#4E9CFF", onAction }: DistributionCardProps) => {
  const { primary, gradient: brandGradient } = getReadableGenieBrandTheme(agent);
  const channels = agent.type === "listener" ? LISTENER_CHANNELS : CHANNELS;

  return (
    <OverviewSectionCard
      title="Distribution"
      subtitle="Channels to reach your genie"
      genieIcon="broadcast"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
    >
      <Link
        to={`/genie-hub/activation/genie/${agent.id}`}
        className="group relative flex flex-col sm:flex-row sm:items-center gap-4 p-6 md:p-7 rounded-2xl mb-4 overflow-hidden border border-transparent text-white transition-all hover:-translate-y-0.5 hover:shadow-xl"
        style={{
          background: brandGradient,
          boxShadow: `0 20px 40px -20px ${withAlpha(primary, "99")}`,
        }}
      >
        <span
          className="pointer-events-none absolute -top-1/3 -right-1/4 w-2/3 h-[170%] rotate-12 bg-white/10 blur-2xl"
          aria-hidden
        />
        <span className="relative w-14 h-14 rounded-2xl bg-white/20 ring-1 ring-white/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
          <img src={BRAND_MASCOT_LARGE} alt="" className="w-[85%] h-[85%] object-contain" />
        </span>
        <div className="relative flex-1 min-w-0">
          <span className="text-[11px] font-bold tracking-[0.16em] uppercase text-white/80 block">
            Get it out there
          </span>
          <div className="text-xl font-bold leading-tight mt-1">Activation Pack</div>
          <p className="text-sm text-white/85 leading-snug mt-1.5">
            QR codes, embed snippet, email signature, and print-ready assets — all in one place.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {["QR codes", "Shareable link", "Posters", "Email signature"].map((chip) => (
              <span
                key={chip}
                className="px-3 py-1 rounded-full bg-white/15 ring-1 ring-white/20 text-xs font-semibold"
              >
                {chip}
              </span>
            ))}
          </div>
        </div>
        <span className="relative self-start sm:self-center w-11 h-11 rounded-full bg-white/20 ring-1 ring-white/30 flex items-center justify-center flex-shrink-0 transition-transform group-hover:translate-x-0.5">
          <ArrowRight className="w-5 h-5" />
        </span>
      </Link>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {channels.map(({ id, label, icon: Icon, description }) => (
          <button
            key={id}
            onClick={() => onAction(id)}
            className="flex flex-col items-start gap-2 p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-white hover:border-gray-300 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-2">
              <Icon className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-900">{label}</span>
            </div>
            <p className="text-xs text-gray-500">{description}</p>
          </button>
        ))}
        <Link
          to={`/genie-hub/activation/genie/${agent.id}#activate-invite`}
          className="flex flex-col items-start gap-2 p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-white hover:border-gray-300 hover:shadow-sm transition-all text-left"
        >
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-900">Invite</span>
          </div>
          <p className="text-xs text-gray-500">Send invites, manage requests and access</p>
        </Link>
      </div>
    </OverviewSectionCard>
  );
};
